import React, { useState, useEffect, useRef } from 'react';

const PullToRefresh = ({ children, onRefresh }) => {
    const [pullDistance, setPullDistance] = useState(0);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const containerRef = useRef(null);
    const startY = useRef(0);
    const isPulling = useRef(false);
    const isScrolling = useRef(false);
    const scrollTimeout = useRef(null);

    const threshold = 100; // Distance in px to trigger refresh

    const handleTouchStart = (e) => {
        // Get the current scroll position
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

        // Only enable pull-to-refresh if at the very top of the page
        if (scrollTop <= 0) {
            startY.current = e.touches[0].pageY;
            isPulling.current = true;
            isScrolling.current = false;
        }
    };

    const handleTouchMove = (e) => {
        if (!isPulling.current || isRefreshing) return;

        const currentY = e.touches[0].pageY;
        const diff = currentY - startY.current;

        // Only pull down if we're at the top and user is pulling down
        if (diff > 0) {
            // Check if any scrollable element is scrolled
            const target = e.target;
            const isScrollable = isElementScrollable(target);

            // If target is scrollable and not at top, don't pull
            if (isScrollable && target.scrollTop > 0) {
                isPulling.current = false;
                return;
            }

            // Prevent default browser behavior only when pulling
            if (e.cancelable) {
                e.preventDefault();
            }

            // Apply resistance to the pull
            const distance = Math.min(diff * 0.4, threshold + 20);
            setPullDistance(distance);
        } else {
            isPulling.current = false;
            setPullDistance(0);
        }
    };

    // Helper function to check if element is scrollable
    const isElementScrollable = (element) => {
        if (!element || element === document.body) return false;

        const style = window.getComputedStyle(element);
        const overflowY = style.overflowY;
        const isScrollable = overflowY === 'auto' || overflowY === 'scroll';

        return isScrollable && element.scrollHeight > element.clientHeight;
    };

    const handleTouchEnd = () => {
        if (!isPulling.current) {
            isPulling.current = false;
            setPullDistance(0);
            return;
        }

        isPulling.current = false;

        if (pullDistance >= threshold && !isRefreshing) {
            triggerRefresh();
        } else {
            setPullDistance(0);
        }
    };

    const triggerRefresh = async () => {
        setIsRefreshing(true);
        setPullDistance(threshold);

        try {
            if (onRefresh) {
                await onRefresh();
            } else {
                // Default behavior: reload page
                window.location.reload();
            }
        } catch (error) {
            console.error('Refresh failed:', error);
        } finally {
            // Wait a bit before resetting to show the refresh indicator
            setTimeout(() => {
                setIsRefreshing(false);
                setPullDistance(0);
            }, 500);
        }
    };

    // Clean up timeout on unmount
    useEffect(() => {
        return () => {
            if (scrollTimeout.current) {
                clearTimeout(scrollTimeout.current);
            }
        };
    }, []);

    return (
        <div
            ref={containerRef}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            style={{
                position: 'relative',
                minHeight: '100vh',
                overflowX: 'hidden',
                touchAction: 'pan-y pinch-zoom' // Allow vertical scrolling
            }}
        >
            {/* Refresh Indicator */}
            <div
                style={{
                    position: 'fixed',
                    top: pullDistance > 0 ? `${Math.max(0, pullDistance - 50)}px` : '-60px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 9999,
                    transition: isPulling.current || isRefreshing ? 'none' : 'top 0.3s cubic-bezier(0.2, 0.9, 0.4, 1.1)',
                    opacity: Math.min(pullDistance / threshold, 1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '44px',
                    height: '44px',
                    borderRadius: '50%',
                    backgroundColor: '#fff',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
                    pointerEvents: 'none',
                }}
            >
                <div
                    className={isRefreshing ? "spin-animation" : ""}
                    style={{
                        width: '24px',
                        height: '24px',
                        border: '3px solid #FD7014',
                        borderTopColor: 'transparent',
                        borderRadius: '50%',
                        transition: 'transform 0.1s linear',
                        transform: isRefreshing ? 'rotate(0deg)' : `rotate(${Math.min((pullDistance / threshold) * 360, 360)}deg)`
                    }}
                />
            </div>

            {/* Content Container */}
            <div
                style={{
                    transform: `translateY(${pullDistance > 0 ? pullDistance * 0.5 : 0}px)`,
                    transition: isPulling.current || isRefreshing ? 'none' : 'transform 0.3s cubic-bezier(0.2, 0.9, 0.4, 1.1)',
                    willChange: 'transform'
                }}
            >
                {children}
            </div>

            <style>
                {`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                .spin-animation {
                    animation: spin 0.8s linear infinite !important;
                }
                `}
            </style>
        </div>
    );
};

export default PullToRefresh;